import type { FC } from 'react'
import { useMemo } from 'react'
import type { SxProps, Theme } from '@mui/material'
import { Box, Tooltip, Typography } from '@mui/material'
import { CopyButton } from '@/common/components'
import { useSystemInfo } from '../hooks'
import { useUptimeContext } from '@/features/status'

function formatUptime(milliseconds: number): string {
  const totalSeconds = Math.floor(milliseconds / 1000)
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60

  const pad = (n: number): string => n.toString().padStart(2, '0')
  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`
}

export const SystemSection: FC = () => {
  const systemInfo = useSystemInfo()
  const { uptime } = useUptimeContext()
  const formattedUptime = useMemo(() => formatUptime(uptime), [uptime])

  return (
    <Box sx={rowsContainerSx}>
      <SystemInfoRow label="User ID" value={systemInfo.userId} />
      <SystemInfoRow label="Backend URL" value={systemInfo.backendUrl} />
      <SystemInfoRow label="Conversation Stream" value={systemInfo.conversationStreamId} />
      <SystemInfoRow label="Activity Stream" value={systemInfo.activityStreamId} />
      <SystemInfoRow label="Uptime" value={formattedUptime} copyable={false} />
    </Box>
  )
}

interface SystemInfoRowProps {
  readonly label: string
  readonly value: string | null
  readonly copyable?: boolean
}

const SystemInfoRow: FC<SystemInfoRowProps> = ({ label, value, copyable = true }) => {
  const displayValue = value ?? '\u2014'

  return (
    <Box sx={rowSx}>
      <Typography variant="caption" sx={labelSx}>
        {label}
      </Typography>
      <Box sx={valueContainerSx}>
        <Tooltip title={displayValue} arrow placement="top">
          <Typography variant="body2" sx={valueSx}>
            {displayValue}
          </Typography>
        </Tooltip>
        {copyable && value && <CopyButton text={value} variant="small" />}
      </Box>
    </Box>
  )
}

const rowsContainerSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  gap: 1.5,
}

const rowSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
}

const labelSx: SxProps<Theme> = {
  color: 'text.disabled',
  fontSize: '0.7rem',
  fontWeight: 500,
  letterSpacing: '0.04em',
  minWidth: { xs: 110, sm: 150 },
  flexShrink: 0,
}

const valueContainerSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: 0.5,
  flex: 1,
  minWidth: 0,
}

const valueSx: SxProps<Theme> = {
  color: 'text.secondary',
  fontFamily: 'monospace',
  fontSize: '0.75rem',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  flex: 1,
}
